import React, { useEffect, useState } from "react";
import { AxButton, AxInput, AxPassword } from "@dynata/axiom3";
import Error from "../../../core/widgets/error";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUpdatePassword } from "./hooks";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import isPasswordCorrect from "../../../services/validation";
import paths from "paths";
import { ToastConfig } from "core/widgets/toastConfig.widget";

const GENERIC_ERROR =
    "There was an error processing your request, please try again later";
const TEMP_PASSWORD_ERROR = "Temporary password required";
const NEW_PASSWORD_ERROR = "New password required";
const EMPTY_EMAIL_ERROR = "Email required";

export const UpdatePasswordPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [error, setError] = useState<string | undefined>();
    const [tempError, setTempError] = useState<string | undefined>();
    const [newPasswordError, setNewPasswordError] = useState<
        string | undefined
    >();
    const [responseError, setResponseError] = useState<string | undefined>();

    const [loading, setLoading] = useState(false);
    const [tempPassword, setTempPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [email, setEmail] = useState("");

    const updatePassword = useUpdatePassword();

    useEffect(() => {
        setEmail(() => searchParams.get("email") ?? "")
        setTempPassword(() => searchParams.get("temp") ?? "")
    }, []);

    const submit = (e: any) => {
        e.preventDefault();

        if (!email.trim()) {
            setError(EMPTY_EMAIL_ERROR);
            return;
        }

        if (!tempPassword.trim()) {
            setTempError(TEMP_PASSWORD_ERROR);
            return;
        }

        if (!newPassword.trim()) {
            setNewPasswordError(NEW_PASSWORD_ERROR);
            return;
        }
        let result = isPasswordCorrect(newPassword);
        if (!result.success) {
            setNewPasswordError(result.error.message);
            return;
        }

        setLoading(true);

        updatePassword.mutate(
            { email, tempPassword, newPassword },
            {
                onSuccess: (_) => {
                    toast.success("Successfully changed password!", ToastConfig);
                    navigate(`/${paths.open.LOGIN}`);
                },
                onError: () => {
                    setResponseError(GENERIC_ERROR);
                },
            },
        );
    };

    return (
        <form className="form-wrapper">
            <div className="reset-fields">
                <AxInput
                    prefix={<i className="fa-regular fa-envelope" />}
                    onChange={(e: any) => {
                        setError(undefined);
                        if (!e.target.value.trim()) {
                            setError(EMPTY_EMAIL_ERROR);
                        }
                        setEmail(e.target.value);
                    }}
                    placeholder="Email"
                    value={email}
                    size="large"
                />
                <Error error={error} />
                <AxPassword
                    prefix={<i className="fa-regular fa-shield" />}
                    onChange={(e: any) => {
                        setTempError(undefined);
                        if (!e.target.value.trim()) {
                            setTempError(TEMP_PASSWORD_ERROR);
                        }
                        setTempPassword(e.target.value);
                    }}
                    placeholder="Temporary Password"
                    value={tempPassword}
                    size="large"
                />
                <Error error={tempError} />
                <AxPassword
                    prefix={<i className="fa-regular fa-key" />}
                    onChange={(e: any) => {
                        setNewPasswordError(undefined);
                        if (!e.target.value.trim()) {
                            setNewPasswordError(NEW_PASSWORD_ERROR);
                        }
                        setNewPassword(e.target.value);
                    }}
                    placeholder="New Password"
                    value={newPassword}
                    size="large"
                    onPressEnter={submit}
                />
                <div className="error-div">
                    <Error error={newPasswordError} />
                </div>
            </div>
            <div className="reset-actions">
                <AxButton
                    disabled={
                        !email.trim() || !newPassword.trim() || !tempPassword.trim()
                            ? "disabled"
                            : ""
                    }
                    loading={loading}
                    onClick={submit}
                    size="large"
                >
                    Update
                </AxButton>
                <Error error={responseError} />
                <AxButton
                    onClick={() => navigate(`/${paths.open.LOGIN}`)}
                    size="large"
                    color="main"
                    type="link"
                >
                    Back to Login
                </AxButton>
            </div>
        </form>
    );
};
